import {
    FAMILY_FRIENDS_ID,
    GIGAKOMBI_SOCS,
    SalesChannel,
    SALESCHANNEL_FAMILY_FRIENDS,
    SALESCHANNEL_SOHO,
    SOHO_ID,
    SOHO_XL_ID,
    TRADE_IN_SOC,
} from '@vfde-fe/sails-constants';
import { getIsTradeInEligible } from '../../DeviceDetail/helpers/getIsTradeInEligible';
import {
    Offer,
    StateProps,
} from '../../DeviceDetail/interface';

/**
 * Get monthly tariff price including all discounts
 * @param offer
 * @param gigakombi
 * @param isTradeIn
 * @param hasInsurance
 * @param salesChannel
 * @param getStairway
 */
export const calculateDiscountedMonthlyPrice = ({ offer, isGigakombi, isTradeIn, hasInsurance, salesChannel, hasSubscription}: Partial<StateProps>, getStairway: boolean = false): number => {
    // We have nothing, go away
    if (!hasSubscription && !hasInsurance) {
        return;
    }

    // We can have an insurance, even if we don't have a subscription (yet), but we don't need to calculate then
    if (!hasSubscription && hasInsurance) {
        return offer.insurance.price.gross;
    }

    const { monetary: stairway = [], subLevelAmount = 0, discounts = {}, insurance, tariffPrice } = offer;

    // Get either stairway (if present) or tariff price as base price to do further calculations on.
    let basePrice = stairway.length > 0 && !getStairway ? stairway[0].gross : tariffPrice.gross;

    // Also, remove sublevel from base price for more generic calculations. Will be added back later, don't worry.
    basePrice -= subLevelAmount;

    // Put all eligible discounts in an array
    const eligibleDiscounts = [];
    isEligibleForGigakombi(basePrice, salesChannel, isGigakombi) && eligibleDiscounts.push(getNegativeValue(getMonetaryDiscount(discounts.monetary, GIGAKOMBI_SOCS)));
    isEligibleForFamilyFriends(salesChannel) && eligibleDiscounts.push(getNegativeValue(getMonetaryDiscount(discounts.monetary, [FAMILY_FRIENDS_ID])));
    isEligibleForTradeIn(isTradeIn, salesChannel) && eligibleDiscounts.push(getNegativeValue(getMonetaryDiscount(discounts.monetary, [ TRADE_IN_SOC ])));
    isEligibleForSoho(basePrice, salesChannel) && eligibleDiscounts.push(getNegativeValue(getMonetaryDiscount(offer.discounts.monetary, [SOHO_ID, SOHO_XL_ID])));

    // Put all additional costs in an array, like insurance
    const eligibleAddons = [subLevelAmount]; // Sublevel is back, you see
    hasInsurance && eligibleAddons.push(insurance.price.gross);

    const price = [...eligibleDiscounts, ...eligibleAddons].reduce((previousValue, currentValue) => previousValue + currentValue, basePrice);

    return price > 0 ? roundToTwoDecimal(price) : 0;
};

/**
 * Calculate stairway price
 * @param state
 */
export const calculateMonthlyPriceWithoutStairway = (state: Partial<StateProps>): number => {
    return calculateDiscountedMonthlyPrice(state, true);
};

/**
 * getMonetaryTradeIn
 */
export const getMonetaryDiscount = (monetaryDiscounts = [], discountIds = []) => {
    const monetaryDiscountsLength = monetaryDiscounts.length;
    let totalDiscount: number = 0;

    for (let i = 0; i < monetaryDiscountsLength; i++) {
        const monetaryDiscount = monetaryDiscounts[i];

        discountIds.forEach((id) => {
            if (monetaryDiscount.id === id) {
                totalDiscount += monetaryDiscount.gross;
            }
        });
    }

    return totalDiscount;
};

const isEligibleForGigakombi = (basePrice: number, salesChannel: SalesChannel, isGigakombi: boolean): boolean => {
    return isGigakombi && !isFamilyFriends(salesChannel) && basePrice > 0;
};

const isEligibleForTradeIn = (isTradeIn, salesChannel: SalesChannel): boolean => {
    return getIsTradeInEligible(salesChannel) && isTradeIn;
};

const isEligibleForFamilyFriends = (salesChannel: SalesChannel): boolean => {
    return isFamilyFriends(salesChannel);
};

const isEligibleForSoho = (basePrice: number, salesChannel: SalesChannel): boolean => {
    return isSoho(salesChannel) && basePrice > 0;
};

const isSalesChannel = (currentSalesChannel: SalesChannel, salesChannelToCheck: SalesChannel): boolean => {
    return currentSalesChannel === salesChannelToCheck;
};

const isSoho = (salesChannel: SalesChannel): boolean => {
    return isSalesChannel(salesChannel, SALESCHANNEL_SOHO);
};

const isFamilyFriends = (salesChannel: SalesChannel): boolean => {
    return isSalesChannel(salesChannel, SALESCHANNEL_FAMILY_FRIENDS);
};

/**
 * Get strike price from offer
 */
export const getStrikePrice = ({ offer, hasInsurance = false, isTradeIn = false, isGigakombi = false }: Partial<StateProps>): number => {
    if (!offer) {
        return;
    }

    let strikePrice = offer.tariffPrice.gross;

    if (hasInsurance) {
        strikePrice += offer.insurance.price.gross;
    }

    if (isTradeIn) {
        strikePrice -= getMonetaryDiscount(offer.discounts.monetary, [TRADE_IN_SOC]);
    }

    return roundToTwoDecimal(strikePrice);
};

/**
 * Check for stairway in offer
 * @param offer
 */
export const hasStairway = (offer: Offer): boolean => {
    const { monetary = [] } = offer;

    return monetary.length > 0;
};

/**
 * Check if a discount is "eligible" for a strike price
 * @param offer
 * @param salesChannel
 * @param isGigakombi
 */
export const shouldShowStrikePrice = ({ offer, salesChannel, isGigakombi = false }: Partial<StateProps>): boolean => {
    return !hasStairway(offer) && ([SALESCHANNEL_SOHO, SALESCHANNEL_FAMILY_FRIENDS].indexOf(salesChannel) !== -1 || isGigakombi);
};

const getNegativeValue = (value: number): number => value * -1;

const roundToTwoDecimal = (numberToRound) => Math.round(numberToRound * 100) / 100;
